import React from 'react'
import styled from 'styled-components'
import type {
  AttendanceOccurrenceSelectionDetails,
  PrescribedDrugsSelectionDetails,
  VariableSelectionDetails as VariableSelectionDetailsInfo,
  VariableWithSelectionPayload,
} from '~/packages/general/variable/selectionDetails.typing'
import { getVariableSelectionDetails } from '~/packages/general/variable/selectionDetails.util'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.mediumGrey};
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkGrey};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const Description = styled.p`
  margin: 0;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.darkText};
`

const CharacteristicList = styled.dl`
  display: grid;
  grid-template-columns: minmax(120px, max-content) 1fr;
  gap: 0.35rem 0.75rem;
  margin: 0;

  dt {
    margin: 0;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.darkText};
  }

  dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.darkGrey};
  }
`

const BulletList = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
  color: ${({ theme }) => theme.colors.darkGrey};

  li + li {
    margin-top: 0.25rem;
  }
`

function renderBaseSections(details: VariableSelectionDetailsInfo) {
  return (
    <>
      {details.description ? (
        <Section>
          <SectionTitle>Descricao</SectionTitle>
          <Description>{details.description}</Description>
        </Section>
      ) : null}

      {details.characteristics?.length ? (
        <Section>
          <SectionTitle>Caracteristicas</SectionTitle>
          <CharacteristicList>
            {details.characteristics.map(characteristic => (
              <React.Fragment key={`${characteristic.label}-${characteristic.value}`}>
                <dt>{characteristic.label}</dt>
                <dd>{characteristic.value}</dd>
              </React.Fragment>
            ))}
          </CharacteristicList>
        </Section>
      ) : null}
    </>
  )
}

function renderAttendanceOccurrenceDetails(details: AttendanceOccurrenceSelectionDetails) {
  return (
    <>
      {renderBaseSections(details)}

      {details.symptoms?.length ? (
        <Section>
          <SectionTitle>Sintomas</SectionTitle>
          <BulletList>
            {details.symptoms.map(symptom => (
              <li key={symptom}>{symptom}</li>
            ))}
          </BulletList>
        </Section>
      ) : null}

      {details.treatments?.length ? (
        <Section>
          <SectionTitle>Tratamentos</SectionTitle>
          <BulletList>
            {details.treatments.map(treatment => (
              <li key={treatment}>{treatment}</li>
            ))}
          </BulletList>
        </Section>
      ) : null}
    </>
  )
}

function renderPrescribedDrugDetails(details: PrescribedDrugsSelectionDetails) {
  return (
    <>
      {renderBaseSections(details)}

      {details.illicitUseJustification ? (
        <Section>
          <SectionTitle>Uso improprio</SectionTitle>
          <Description>{details.illicitUseJustification}</Description>
        </Section>
      ) : null}
    </>
  )
}

const detailRenderers: Record<string, (details: VariableSelectionDetailsInfo) => React.ReactNode> = {
  attendanceOccurrence: details =>
    renderAttendanceOccurrenceDetails(details as AttendanceOccurrenceSelectionDetails),
  prescribedDrugs: details => renderPrescribedDrugDetails(details as PrescribedDrugsSelectionDetails),
}

interface VariableSelectionDetailsProps {
  variable: VariableWithSelectionPayload
}

export default function VariableSelectionDetails({ variable }: VariableSelectionDetailsProps) {
  const details = getVariableSelectionDetails(variable)

  if (!details) {
    return null
  }

  const renderer = detailRenderers[details.kind] ?? renderBaseSections

  return <Container>{renderer(details)}</Container>
}
